import React from 'react';
import {
  TouchableOpacity,
  Text,
  StyleSheet,
  ActivityIndicator,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from 'react-native-paper';

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  style?: any;
}

export const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  icon,
  style,
}) => {
  const theme = useTheme();

  const getButtonStyle = () => {
    const baseStyle = [styles.button, styles[size]];

    switch (variant) {
      case 'primary':
        return [
          ...baseStyle,
          {
            backgroundColor: disabled
              ? theme.colors.outline
              : theme.colors.primary,
          },
        ];
      case 'secondary':
        return [
          ...baseStyle,
          {
            backgroundColor: disabled
              ? theme.colors.outline
              : theme.colors.secondary,
          },
        ];
      case 'outline':
        return [
          ...baseStyle,
          {
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: disabled ? theme.colors.outline : theme.colors.primary,
          },
        ];
      default:
        return baseStyle;
    }
  };

  const getTextStyle = () => {
    const baseStyle = [styles.text, styles[`${size}Text`]];

    switch (variant) {
      case 'primary':
        return [
          ...baseStyle,
          { color: disabled ? theme.colors.onSurface : theme.colors.onPrimary },
        ];
      case 'secondary':
        return [
          ...baseStyle,
          {
            color: disabled ? theme.colors.onSurface : theme.colors.onSecondary,
          },
        ];
      case 'outline':
        return [
          ...baseStyle,
          { color: disabled ? theme.colors.outline : theme.colors.primary },
        ];
      default:
        return baseStyle;
    }
  };

  return (
    <TouchableOpacity
      style={[getButtonStyle(), style]}
      onPress={onPress}
      disabled={disabled || loading}
      activeOpacity={0.7}
    >
      <View style={styles.content}>
        {loading ? (
          <ActivityIndicator
            size='small'
            color={
              variant === 'outline'
                ? theme.colors.primary
                : theme.colors.onPrimary
            }
          />
        ) : (
          <>
            {icon && (
              <Ionicons
                name={icon as any}
                size={16}
                color={
                  variant === 'outline'
                    ? theme.colors.primary
                    : theme.colors.onPrimary
                }
                style={styles.icon}
              />
            )}
            <Text style={getTextStyle()}>{title}</Text>
          </>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  button: {
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    fontWeight: '500',
    textAlign: 'center',
  },
  icon: {
    marginRight: 8,
  },
  // Size styles
  small: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  medium: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  large: {
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  smallText: {
    fontSize: 14,
  },
  mediumText: {
    fontSize: 16,
  },
  largeText: {
    fontSize: 18,
  },
});
